import type {
    EvmEventConfig,
    EvmEventHandlerMap,
    EvmEventOperation,
    EvmEventPayloadMap,
    EvmExtractKey,
    EvmExtractPayload
} from './types';

export class EvmEventEmitter {
    /**
     * Хранит соответствие типа события и подписчиков.
     *
     * **Структура:**
     *
     * {
     *   "test1": Set(["MarketplaceComponent", "UserComponent"]),
     *   "auth": Set(["AuthComponent"])
     * }
     *
     * - **Ключ**: `type` (например, `"test1"`, `"auth"`)
     * - **Значение**: `Set<string | number>` (уникальные ключи подписчиков)
     *
     */
    private eventKeys: Map<keyof EvmEventPayloadMap, Set<string | number>> = new Map();

    /**
     * Хранит обработчики событий, привязанные к подписчикам.
     *
     * **Структура:**
     *
     * {
     *   "test1": Map({
     *      "MarketplaceComponent": { onSuccess, onError, onLoad },
     *      "UserComponent": { onSuccess, onError, onLoad }
     *   }),
     *   "auth": Map({
     *      "AuthComponent": { onSuccess, onError, onLoad }
     *   })
     * }
     *
     * - **Ключ**: `type` (например, `"test1"`, `"auth"`)
     * - **Значение**: `Map<string | number, EvmEventConfig>` (где `EvmEventConfig` содержит `onSuccess`, `onError`, `onLoad`)
     */
    private eventHandlers: Map<keyof EvmEventPayloadMap, EvmEventHandlerMap<keyof EvmEventPayloadMap>> = new Map();

    /**
     * Подписывает обработчик на событие.
     *
     * @template TType Тип события (например, `"prepareOrders"`, `"auth"`)
     * @param {TType} type - Тип события, на которое подписываемся.
     * @param {EvmEventConfig<TType>} config - Конфигурация подписки (обработчики `onSuccess`, `onError`, `onLoad`).
     *
     * **Пример подписки:**

     * EventEmitter.on("prepareOrders", {
     *   key: "MarketplaceComponent",
     *   onSuccess: (payload) => console.log("Получены ордера:", payload.orders),
     *   onError: (error) => console.error("Ошибка:", error),
     *   onLoad: (loading) => console.log("Загрузка:", loading)
     * });
     */
    on<TType extends keyof EvmEventPayloadMap>(type: TType, config: EvmEventConfig<TType>): void {
        if (!this.eventKeys.has(type)) {
            this.eventKeys.set(type, new Set());
        }

        if (!this.eventHandlers.has(type)) {
            this.eventHandlers.set(type, new Map());
        }

        const keys = this.eventKeys.get(type);
        const handlers = this.eventHandlers.get(type) as EvmEventHandlerMap<TType>;

        if (!keys || !handlers) return;

        if (!keys.has(config.key)) {
            keys.add(config.key);
            handlers.set(config.key, {
                onSuccess: config.onSuccess,
                onError: config.onError,
                onLoad: config.onLoad
            });

            if (config.onLoad) {
                try {
                    config.onLoad(false);
                }
                catch (error: unknown) {
                    console.error(`Ошибка в onLoad для ${String(type)}:`, error);
                }
            }
        }
        // else {
        //     console.info(`Подписчик с ключом ${config.key} уже существует для события ${type}`);
        // }
    }

    /**
     * Эмитит событие, выполняет переданную операцию (`operation`) и передаёт результат подписчикам.
     *
     * @template TType Тип события (например, `"prepareOrders"`, `"auth"`)
     * @param {TType} type - Тип события, которое эмитится.
     * @param {() => Promise<ExtractPayload<TType>> | () => ExtractPayload<TType>} operation - Операция, выполняемая при эмите.
     * @param payload
     * @returns { Promise<EvmExtractResult<TType>>} Результат выполнения `operation()`.
     *
     * **Пример эмита:**
     *
     * const res = await EventEmitter.emit("prepareOrders", async () =>
     *   new Promise((resolve) => setTimeout(() => resolve({ orders: [{ id: 1 }] }), 1000))
     * );
     */
    async emit<TType extends keyof EvmEventPayloadMap>(
        type: TType,
        operation: EvmEventOperation<TType>,
        payload?: EvmExtractPayload<TType>
    ) {
        const keys = this.eventKeys.get(type);
        const handlers = this.eventHandlers.get(type) as EvmEventHandlerMap<TType> | undefined;

        const rawResult = operation();
        const isAsync = rawResult instanceof Promise;

        if (!keys || !handlers || keys.size === 0) {
            return isAsync
                ? await rawResult
                : rawResult;
        }

        keys.forEach((key) => {
            const handler = handlers.get(key);
            if (handler?.onLoad) handler.onLoad(true);
        });

        // console.info(`Emit события ${type} с ключами:`, Array.from(keys || []));

        try {
            const result = isAsync
                ? await rawResult
                : rawResult;

            keys.forEach((key) => {
                const handler = handlers.get(key);
                if (handler) {
                    if (handler.onLoad) handler.onLoad(false);
                    handler.onSuccess(result, payload);
                    // console.info(`onSuccess для ключа ${key} вызван`);
                }
            });

            return result;
        }
        catch (error) {
            keys.forEach((key) => {
                const handler = handlers.get(key);
                if (handler) {
                    if (handler.onLoad) handler.onLoad(false);
                    if (handler.onError) handler.onError(error);
                }
            });

            throw error;
        }
    }

    /**
     * Отписывает подписчика от события.
     *
     * @template TType Тип события (например, `"prepareOrders"`, `"auth"`)
     * @param {TType} type - Тип события, от которого отписываемся.
     * @param {EvmExtractKey<TType>} key - Уникальный идентификатор подписчика.
     *
     * **Пример отписки:**
     *
     * EventEmitter.off("prepareOrders", "MarketplaceComponent");
     */
    off<TType extends keyof EvmEventPayloadMap>(type: TType, key: EvmExtractKey<TType>): void {
        const keys = this.eventKeys.get(type);
        const handlers = this.eventHandlers.get(type) as EvmEventHandlerMap<TType> | undefined;

        if (!keys || !handlers) return;

        keys.delete(key);
        handlers.delete(key);

        if (keys.size === 0) {
            this.eventKeys.delete(type);
        }

        if (handlers.size === 0) {
            this.eventHandlers.delete(type);
        }
    }
}
